import * as React from 'react';
import * as Images from 'pos/Images';

import { Result, SessionProvider } from 'core/infrastructure';
import { Preconditions } from 'core/infrastructure-next';
import { Command } from 'pos/common';
import { Permission, Res, MenuConfigurationDTO, MenuGroupConfigurationDTO, ProductForMenuConfigurationDTO } from 'core/api';
import { ActionButtonDefinition, PosScreenClassComponent, ScreenSize } from 'pos/ui';
import selectProduct from './selectProductForMenuConfiguration';
import { CatalogueFacade } from 'pos/facades';
import { Cols, Col, Flex, ActionButton } from 'core/ui';
import { Modal } from 'core/ui-next/modal';

export function MenuList({ menus, onSelectMenu, selectedMenu }: {
	menus: MenuConfigurationDTO[];
	onSelectMenu: (menu: MenuConfigurationDTO) => void;
	selectedMenu: Nullable<MenuConfigurationDTO>;
}): React.ReactNode {
	const menuList = menus.map(m => {
		const selectedClass = selectedMenu && selectedMenu.MenuId === m.MenuId ? 'selected' : '';
		return <li className={selectedClass} key={m.MenuId} onClick={() => onSelectMenu(m)}>
			<Cols>
				<Col className='text-left text-18'>
					{m.MenuName}
				</Col>
			</Cols>
		</li>;
	});

	return <Flex.Vertical>
		<Flex.Item>
			<Cols className='list-header'>
				<Col>
					{Res.Web.CONFIGURE_MENU_MENU_LBL()}
				</Col>
			</Cols>
		</Flex.Item>
		<Flex.Item size='*'>
			<ul className='list'>
				{menuList}
			</ul>
		</Flex.Item>
	</Flex.Vertical>;
}

function MenuGroupList({ groups, onSelectGroup, selectedGroup }: {
	groups: MenuGroupConfigurationDTO[];
	onSelectGroup: (group: MenuGroupConfigurationDTO) => void;
	selectedGroup: Nullable<MenuGroupConfigurationDTO>;
}): React.ReactNode {
	const groupList = groups.map(g => {
		const selectedClass = selectedGroup && selectedGroup.ItemsCategoryId === g.ItemsCategoryId ? 'selected' : '';
		return <li className={selectedClass} key={g.ItemsCategoryId} onClick={() => onSelectGroup(g)}>
			<Cols>
				<Col className='text-left text-18'>
					{g.GroupName}
				</Col>
			</Cols>
		</li>;
	});

	return <Flex.Vertical>
		<Flex.Item>
			<Cols className='list-header'>
				<Col>
					{Res.Web.CONFIGURE_MENU_GROUPS_LBL()}
				</Col>
			</Cols>
		</Flex.Item>
		<Flex.Item size='*'>
			<ul className='list'>
				{groupList}
			</ul>
		</Flex.Item>
	</Flex.Vertical>;
}

function ProductList({ products, onSelectProduct, selectedProduct }: {
	products: ProductForMenuConfigurationDTO[];
	onSelectProduct: (product: ProductForMenuConfigurationDTO) => void;
	selectedProduct: Nullable<ProductForMenuConfigurationDTO>;
}): React.ReactNode {
	const productList = products.map(p => {
		const selectedClass = selectedProduct && selectedProduct.Id === p.Id ? 'selected' : '';
		return <li key={p.Id} className={selectedClass} onClick={() => onSelectProduct(p)} >
			<Cols>
				<Col className='text-left text-18'>
					{p.Name}
				</Col>
			</Cols>
		</li>;
	});

	return <Flex.Vertical>
		<Flex.Item>
			<Cols className='list-header'>
				<Col>
					{Res.Web.CONFIGURE_MENU_PRODUCTS_LBL()}
				</Col>
			</Cols>
		</Flex.Item>
		<Flex.Item size='*'>
			<ul className='list'>
				{productList}
			</ul>
		</Flex.Item>
	</Flex.Vertical>;
}

type Props = { menus: MenuConfigurationDTO[] } & Modal.Actions<void>;

type State = { selectedMenu: Nullable<MenuConfigurationDTO>, selectedGroup: Nullable<MenuGroupConfigurationDTO>, products: ProductForMenuConfigurationDTO[], selectedProduct: Nullable<ProductForMenuConfigurationDTO> };

class ConfigureMenuScreen extends PosScreenClassComponent<Props, State> {


	createInitialState(_props: Props): Nullable<State> {
		return { selectedMenu: null, selectedGroup: null, products: [], selectedProduct: null };
	}

	protected getScreenId(): string {
		return 'CONFIGURE_MENU';
	}

	protected getTitle(): string {
		return Res.Web.CONFIGURE_MENU();
	}

	protected getImage(): string | SvgIcon {
		return Images.ComposeMenu;
	}

	protected getSize(): ScreenSize {
		return 'full-screen';
	}

	protected getActionButtonsDefs(): Nullable<ActionButtonDefinition>[] {

		const publishBtn = SessionProvider.isSmartMenuEnabled() ? { title: Res.Web.PUBLISH_DIGITAL_MENU_BTN(), image: Images.PublishDigitalMenu, action: this.onPublisAllDigitalMenus } : null;
		const printMenusBtn = { title: Res.Web.PRINT_MENU_BTN(), image: Images.PrintTicket, action: this.onPrintMenus };
		return [
			this.cancelButton(),
			null,
			null,
			null,
			printMenusBtn,
			publishBtn
		];
	}

	onSelectMenu = async (menu: MenuConfigurationDTO) => {
		this.setState({ selectedMenu: menu });
		if (menu.MenuGroups && menu.MenuGroups.length > 0)
			await this.onSelectGroup(menu.MenuGroups[0]);
	};

	onSelectGroup = async (group: MenuGroupConfigurationDTO) => {
		const products = await CatalogueFacade.getProductsByMenuGroup(group);
		const selectedProduct = products.length > 0 ? products[0] : null;
		this.setState({ selectedGroup: group, products, selectedProduct });
	};

	onSelectProduct = (product: ProductForMenuConfigurationDTO) => {
		this.setState({ selectedProduct: product });
	};

	removeSelectedProduct = async () => {
		if (this.state.selectedProduct === null)
			return;

		await CatalogueFacade.removeProductFromMenuGroup(this.state.selectedGroup!, this.state.selectedProduct!);
		await this.onSelectGroup(this.state.selectedGroup!);
	};

	addProduct = async () => {
		if (this.state.selectedGroup === null)
			return;

		const productResult = await selectProduct(this.state.selectedGroup);
		if (!productResult.ok)
			return;

		await this.onSelectGroup(this.state.selectedGroup!);

	};

	onPublisAllDigitalMenus = async () => {
		await Command.execute('publicAllDigitalMenus');
	};

	onPrintMenus = async () => {
		await this.execute({ action: () => Command.execute('printMenu', this.props.menus, this.state.selectedMenu!) });
	};

	protected getContent(): React.ReactNode {

		const buttons = <Flex.Vertical className='p-m'>
			<Flex.Item className='m-t-xl'>
				<ActionButton className='btn-block' text={Res.Web.CONFIGURE_MENU_ADD_BTN()} image={Images.Add} onClick={() => this.addProduct()} />
			</Flex.Item>
			<Flex.Item className='m-t-sm'>
				<ActionButton className='btn-block' text={Res.Web.CONFIGURE_MENU_REMOVE_BTN()} image={Images.Delete} onClick={() => this.removeSelectedProduct()} />
			</Flex.Item>
		</Flex.Vertical>;

		return <Flex.Horizontal>
			<Flex.Item size='*' className='p-m'>
				<MenuList menus={this.props.menus} selectedMenu={this.state.selectedMenu} onSelectMenu={this.onSelectMenu} />
			</Flex.Item>
			<Flex.Item size='*' className='p-m'>
				<MenuGroupList groups={this.state.selectedMenu ? this.state.selectedMenu.MenuGroups : []} selectedGroup={this.state.selectedGroup} onSelectGroup={this.onSelectGroup} />
			</Flex.Item>
			<Flex.Item size='*' className='p-m'>
				<ProductList products={this.state.products} selectedProduct={this.state.selectedProduct} onSelectProduct={this.onSelectProduct} />
			</Flex.Item>
			<Flex.Item>
				{buttons}
			</Flex.Item>
		</Flex.Horizontal>;
	}

	async onComponentDidMount(): Promise<void> {
		if (this.props.menus && this.props.menus.length > 0)
			await this.onSelectMenu(this.props.menus[0]);
	}

	onComponentWillUnmount(): void {
	}
}

declare global {
	interface CommandType {
		configureMenu: () => Promise<Result>
	}
}

Command.register('configureMenu', Preconditions.and(Preconditions.ensure(Permission.Menu_Configure), Preconditions.ensureIsNotLite), async (): Promise<Result> => {
	const menusToConfigure = await CatalogueFacade.getMenus();

	await Modal.show(ConfigureMenuScreen, { menus: menusToConfigure });

	return Result.ok();
});